import { Button } from '@99designs/common/components/Button';
import { ProgressBar } from '@99designs/common/components/ProgressBar';
import { formatMessage } from '@99designs/common/i18n';
import { trackEvent } from '@99designs/common/utils/platform';
import {
    ApolloCache,
    ApolloClient,
    ApolloProvider,
    gql,
    InMemoryCache,
    MutationHookOptions,
    useMutation,
} from '@apollo/client';
import Bugsnag from 'common/utils/Bugsnag';
import Cookies from 'cookies-js';
import { BundlePrice } from 'interactive/bundle-options';
import {
    defaultConfig as config,
    trackCompletedStep,
} from 'interactive/get-started-steps-configuration';
import useAppState, { clearAppState } from 'interactive/hooks/useAppState';
import { LogoTypeKey } from 'interactive/logo-types';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { IntlProvider } from 'react-intl';
import Waypoint from 'react-waypoint';

interface AppProps {
    categoryKey: string;
    logoDesignBundlePrices: BundlePrice[];
}

type InitProps = AppProps & {
    container: HTMLDivElement;
};

export function init({ categoryKey, container, logoDesignBundlePrices }: InitProps) {
    const client = new ApolloClient({
        uri: '/app/graphql',
        headers: {
            'X-Csrf-Token': Cookies.get('_99csrf') || '',
        },
        cache: new InMemoryCache(),
    });

    ReactDOM.render(
        <ApolloProvider client={client}>
            <IntlProvider locale={document.documentElement.lang}>
                <App categoryKey={categoryKey} logoDesignBundlePrices={logoDesignBundlePrices} />
            </IntlProvider>
        </ApolloProvider>,
        container
    );
}

const App = ({ categoryKey, logoDesignBundlePrices }: AppProps) => {
    const [isSeenByUser, setIsSeenByUser] = useState(false);
    const [animation, setAnimation] = useState(true);
    const stateKey = `get-started-interactive:${categoryKey}`;
    const [state, setState] = useAppState(stateKey, {
        step: 1,
        logoTypes: [],
        bundle: 'logo-design',
    });

    const { step, name, industry, logoTypes, bundle } = state;
    const { maxSteps, StepComponent, TitleComponent } = config;
    const comments = getComments(logoTypes);

    const buildUrl = () => {
        const launchCategory = Cookies.get('99x_labs') === 'enabled' ? bundle : categoryKey; // TODO: remove this line and use `bundle` var instead when bundles are ready
        const businessName = name ? encodeURIComponent(name) : '';
        const businessIndustry = industry || '';

        return `/launch/${launchCategory}?business_name=${businessName}&business_industry=${businessIndustry}&comments=${comments}`;
    };

    const goToNextStep = () => {
        trackCompletedStep(state, categoryKey);
        setState({
            ...state,
            step: step + 1,
        });
    };

    const handleCTAToInteractive = () => {
        if (step == 1) {
            goToNextStep();
        } else {
            // Replay the current step animation
            setAnimation(false);
            setAnimation(true);
        }
    };

    useEffect(() => {
        window.addEventListener('CtaToInteractive', handleCTAToInteractive);

        return () => {
            window.removeEventListener('CtaToInteractive', handleCTAToInteractive);
        };
    }, [step]);

    const CREATE_BRIEF = gql`
        mutation CreateBrief($fields: Map!) {
            brief: createBriefWithSlug(slug: "logo-web-bundle", fields: $fields) {
                id
            }
        }
    `;

    const [createBriefMutation, { loading, reset }] = useMutation(CREATE_BRIEF, {
        variables: {
            fields: {
                businessName: name,
                industry,
                comments,
            },
        },
    } as MutationHookOptions<any, Record<string, any>, Record<string, any>, ApolloCache<any>>);

    interface CreateBriefMutationData {
        brief: { id: number };
    }

    return (
        <Waypoint
            topOffset={'200px'}
            bottomOffset={'200px'}
            fireOnRapidScroll={false}
            onEnter={() => {
                if (!isSeenByUser) {
                    trackEvent('Viewed Prelaunch Interactive', {
                        category: categoryKey,
                    });
                    setIsSeenByUser(true);
                }
            }}
        >
            <div className="get-started-interactive">
                <div className="get-started-interactive__header">
                    <div className="get-started-interactive__header__title">
                        <TitleComponent step={step} />
                    </div>
                    <div className="get-started-interactive__header__progress">
                        <ProgressBar progress={(step / maxSteps) * 100} variations={['small']} />
                    </div>
                </div>
                <div className="get-started-interactive__body">
                    <div className="get-started-interactive__body__inner">
                        <StepComponent
                            step={step}
                            categoryKey={categoryKey}
                            logoDesignBundlePrices={logoDesignBundlePrices}
                            currentState={state}
                            onSave={newState => setState(newState)}
                            hasAnimation={animation}
                            resetAnimation={() => setAnimation(false)}
                        />
                    </div>
                </div>
                {step > 1 && (
                    <div className="get-started-interactive__footer">
                        <div className="get-started-interactive__footer__nav">
                            <div className="get-started-interactive__footer__nav__prev">
                                <button
                                    className="icon-button icon-button--lighter icon-button--simple"
                                    onClick={() =>
                                        setState({
                                            ...state,
                                            step: step - 1,
                                        })
                                    }
                                >
                                    <span className="icon icon--left-2" />
                                </button>
                            </div>
                            <div className="get-started-interactive__footer__nav__next">
                                {step === maxSteps ? (
                                    <Button
                                        href={buildUrl()}
                                        variations={['jumbo', 'primary', 'responsive']}
                                        isSaving={loading}
                                        onClick={e => {
                                            trackCompletedStep(state, categoryKey);
                                            clearAppState(stateKey);
                                            if (bundle === 'logo-web-bundle') {
                                                e.preventDefault();
                                                createBriefMutation()
                                                    .then(
                                                        ({
                                                            data,
                                                        }: {
                                                            data: CreateBriefMutationData;
                                                        }) => {
                                                            if (
                                                                data &&
                                                                data.brief &&
                                                                data.brief.id
                                                            ) {
                                                                window.location.assign(
                                                                    `/briefs/${data.brief.id}`
                                                                );
                                                            }
                                                        }
                                                    )
                                                    .catch(error => {
                                                        reset();
                                                        Bugsnag.notify(
                                                            'interactive-createBriefMutation',
                                                            {
                                                                metaData: {
                                                                    apolloError: error,
                                                                    variables: {
                                                                        fields: {
                                                                            businessName: name,
                                                                            industry,
                                                                            comments,
                                                                        },
                                                                    },
                                                                },
                                                            }
                                                        );
                                                    });
                                            }
                                        }}
                                    >
                                        {__('Continue')}
                                    </Button>
                                ) : (
                                    <Button
                                        variations={['jumbo', 'primary', 'responsive']}
                                        onClick={goToNextStep}
                                    >
                                        {__('Continue')}
                                    </Button>
                                )}
                            </div>
                        </div>
                        {step === maxSteps ? (
                            <div className="get-started-interactive__footer__bottom-line">
                                <div className="paragraph paragraph--small paragraph--soft paragraph--no-margin">
                                    {__('Discover your brand style…')}
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        </Waypoint>
    );
};

const getComments = (logoTypes: LogoTypeKey[]): string => {
    if (logoTypes.length === 0) {
        return '';
    }

    if (logoTypes.indexOf('open-to-ideas') !== -1) {
        if (logoTypes.length === 1) {
            return __('I’m open to ideas on the type of logo that’s best for my brand.');
        } else {
            return formatMessage(
                __('I’m mostly interested in {logoTypes} logos, but I’m open to all ideas.'),
                {
                    logoTypes: specificLogoTypes(logoTypes),
                }
            );
        }
    }

    return formatMessage(__('I’m interested in {logoTypes} logos.'), {
        logoTypes: specificLogoTypes(logoTypes),
    });
};

const specificLogoTypes = (logoTypes: LogoTypeKey[]) => {
    const types = logoTypes.filter(logoType => logoType !== 'open-to-ideas');
    const length = types.length;

    if (length > 1) {
        return formatMessage(__('{firstLogoTypes} and {lastLogoType}'), {
            firstLogoTypes: types.slice(0, length - 1).join(', '),
            lastLogoType: types[length - 1],
        });
    } else {
        return types[0];
    }
};
